import React from 'react';
import { Zap } from 'lucide-react';

/**
 * FastByNoFeeMarketing — reusable trust message used across surfaces.
 *
 * Three render modes:
 *   - "inline-hint"     Small card placed near owner-facing config
 *                       (Onboarding Setup, Dashboard Settings).
 *   - "feature-row"     Bullet for "Why Restaurants Choose NoFee" lists.
 *   - "hero-card"       Full marketing card for the landing page hero.
 *
 * Voice: trust + reliability, NEVER speed. Aligned with FAST_PROGRAM.md
 * spec — "On-Time Delivery Verified · Verified by Real Customers ·
 * Delivered As Promised".
 */
const FastByNoFeeMarketing = ({ variant = 'inline-hint', className = '', testid = 'fast-marketing' }) => {
  if (variant === 'inline-hint') {
    return (
      <div
        data-testid={`${testid}-hint`}
        className={`rounded-xl border border-yellow-400/30 bg-yellow-400/[0.06] px-4 py-3 ${className}`}
      >
        <div className="flex items-center gap-2 text-white text-[12px] font-black uppercase tracking-[0.16em]">
          <Zap className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
          Earn On-Time Delivery Recognition
        </div>
        <p className="text-[12.5px] text-white/85 font-semibold mt-1.5">On-Time Delivery Verified</p>
        <p className="text-[12.5px] text-white/65 leading-relaxed mt-1">
          The delivery times you configure are shown to customers. After each order, customers verify whether it arrived within your promised time.
          Build customer trust, and receive more orders.
        </p>
      </div>
    );
  }

  if (variant === 'feature-row') {
    return (
      <div
        data-testid={`${testid}-feature`}
        className={`flex items-start gap-3 ${className}`}
      >
        <span className="w-9 h-9 rounded-lg bg-yellow-400/15 border border-yellow-400/30 flex items-center justify-center flex-shrink-0">
          <Zap className="w-4 h-4 text-yellow-400 fill-yellow-400" />
        </span>
        <div>
          <p className="font-black text-white text-[12px] uppercase tracking-[0.14em]">⚡ Fast by NoFee</p>
          <p className="font-bold text-white text-[14px] mt-0.5">On-Time Delivery Verified</p>
          <p className="text-[12.5px] text-white/65 leading-relaxed mt-1">
            Earn customer trust through verified on-time deliveries. Customers confirm each order, so reliability becomes your competitive advantage.
          </p>
        </div>
      </div>
    );
  }

  if (variant === 'hero-card') {
    return (
      <div
        data-testid={`${testid}-hero`}
        className={`rounded-2xl border border-yellow-400/30 bg-gradient-to-br from-yellow-400/[0.08] via-zinc-950 to-zinc-950 p-6 ${className}`}
      >
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-yellow-400/15 border border-yellow-400/30 text-white text-[11px] font-black uppercase tracking-[0.16em] mb-3">
          <Zap className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
          Fast by NoFee
        </div>
        <h3 className="text-2xl sm:text-[28px] font-bold text-white leading-tight">
          On-Time Delivery Verified
        </h3>
        <p className="text-white/65 mt-2 text-[14px] leading-relaxed">
          <strong className="text-white">Verified by Real Customers. Delivered As Promised.</strong>
          <br />
          Restaurants earn trust by consistently delivering within the time they promise. Customers confirm each order after delivery — and that verification creates trust that converts into more orders.
        </p>
      </div>
    );
  }

  return null;
};

export default FastByNoFeeMarketing;
